import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SwatchIcon, UserCircleIcon } from '@heroicons/react/24/outline'
import LanguageSelector from '../../../shared/components/LanguageSelector'
import { useTheme } from '../../../shared/hooks/useTheme'

type ThemeOption = 'light' | 'dark' | 'system'

const THEME_OPTIONS: ThemeOption[] = ['light', 'dark', 'system']

/** 사용자 설정 — 화면 언어와 테마 */
export default function SettingsPage() {
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()

  return (
    <div className="container mx-auto max-w-3xl px-6 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
        {t('settings.title')}
      </h1>
      <p className="mt-2 text-gray-600 dark:text-gray-300">
        {t('settings.description')}
      </p>

      <div className="mt-8 space-y-4">
        <LanguageSelector withLabel />

        <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
                <SwatchIcon className="h-5 w-5 text-gray-400" />
                {t('settings.theme.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('settings.theme.description')}
              </p>
            </div>

            <div
              role="radiogroup"
              aria-label={t('settings.theme.title')}
              className="inline-flex rounded-xl border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-800"
            >
              {THEME_OPTIONS.map(option => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={theme === option}
                  onClick={() => setTheme(option)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                    theme === option
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(`settings.theme.${option}`)}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
                <UserCircleIcon className="h-5 w-5 text-gray-400" />
                {t('settings.account.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('settings.account.description')}
              </p>
            </div>
            <Link
              to="/profile"
              className="shrink-0 rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {t('settings.account.openProfile')}
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}
